import { handleLogout } from '@/api/axiosInstance';
import { ApiOutlined, DollarOutlined, EditOutlined, HistoryOutlined, KeyOutlined, LoginOutlined, MoonOutlined, QuestionOutlined, SettingOutlined, SunOutlined, UserDeleteOutlined } from '@ant-design/icons';
import { App, Button, Modal, Space, Switch, Tooltip } from 'antd';
import React, { useEffect } from 'react';
import GuideDialog from './guideDialog';
import ResetPassword from './resetPassword';
import FeedbackModal from './feedback';
import SignalIndicator from './signal';
import DeregisterAccount from './deregisterAccount';
import ApiAccess from './apiAccess';
import PaymentHistoryDialog from './paymentHistoryDialog';
import PaymentDialog from './paymentDialog';
import { useTranslation } from 'react-i18next';
import { checkRole } from '@/api/admin';
import { enableApiAccess, enablePayment } from '@/config/branding';
import { useThemeStore } from '@/utils/themeStore';
import { useAuthCapabilities } from '@/utils/authCapabilities';

const AppNavigator: React.FC = () => {
    const { modal } = App.useApp();
    const { t } = useTranslation('common');
    const { theme, toggleTheme } = useThemeStore();
    // Both flows below are refused by the backend when accounts are managed on
    // the host, and both depend on an emailed code a synthesized address cannot
    // receive, so the entries are hidden rather than left to fail.
    const authCaps = useAuthCapabilities();
    const canResetPassword = authCaps.self_service.password_reset;
    const canDeleteAccount = authCaps.self_service.account_deletion;

    const showConfirm = () => {
        modal.confirm({
            title: t('app_setting.confirm_logout'),
            content: t('app_setting.logout_confirm_content'),
            onOk: handleLogout,
            okText: t('app_setting.yes'),
            cancelText: t('app_setting.no'),
        });
    };

    const [showGuide, setShowGuide] = React.useState(false);
    const [showResetPassword, setShowResetPassword] = React.useState(false);
    const [showFeedback, setShowFeedback] = React.useState(false);
    const [showHelpModal, setShowHelpModal] = React.useState(false);
    const [showSettingsModal, setShowSettingsModal] = React.useState(false);
    const [showDeregister, setShowDeregister] = React.useState(false);
    const [showApiAccess, setShowApiAccess] = React.useState(false);
    const [showPaymentButtons, setShowPaymentButtons] = React.useState(false);
    const [showPaymentHistory, setShowPaymentHistory] = React.useState(false);
    const [showPayment, setShowPayment] = React.useState(false);

    useEffect(() => {
        if (!enablePayment) return;
        const checkUserRole = async () => {
            const rolesToCheck = ['international_request', 'academic_pro_request', 'pro_request', 'trial_request'];
            const response = await checkRole(rolesToCheck);
            if (response?.ok && response.data) {
                rolesToCheck.forEach(role => {
                    if (response.data[role]) {
                        setShowPaymentButtons(true);
                    }
                });
            }
        };
        checkUserRole();
    }, [showSettingsModal]);

    const handleFeedback = () => {
        setShowFeedback(true);
        setShowHelpModal(false);
    };

    const handleCloseFeedback = () => {
        setShowFeedback(false);
    };

    const handleGuide = () => {
        setShowGuide(true);
        setShowHelpModal(false);
    };

    const handleApiAccess = () => {
        setShowApiAccess(true);
        setShowSettingsModal(false);
    };

    const handleResetPassword = () => {
        setShowResetPassword(true);
        setShowSettingsModal(false);
    };

    const handleLogoutClick = () => {
        showConfirm();
        setShowSettingsModal(false);
    };

    const handleDeregister = () => {
        setShowDeregister(true);
        setShowSettingsModal(false);
    };

    return (
        <div className='flex flex-col py-4 items-center relative'>
            <GuideDialog open={showGuide} onClose={() => setShowGuide(false)} />
            <ResetPassword open={showResetPassword} onClose={() => setShowResetPassword(false)} />
            <FeedbackModal visible={showFeedback} onClose={handleCloseFeedback} />
            <DeregisterAccount open={showDeregister} onClose={() => setShowDeregister(false)} />
            {enableApiAccess && <ApiAccess open={showApiAccess} onClose={() => setShowApiAccess(false)} />}
            {enablePayment && <>
                <PaymentHistoryDialog visible={showPaymentHistory} onClose={() => setShowPaymentHistory(false)} />
                <PaymentDialog visible={showPayment} onClose={() => setShowPayment(false)} />
            </>}

            <Modal
                title={t('app_setting.help')}
                open={showHelpModal}
                onCancel={() => setShowHelpModal(false)}
                footer={null}
                width={300}
            >
                <Space orientation='vertical' className='w-full'>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={handleFeedback}
                        className='flex items-center justify-start w-full h-12 text-left'
                    >
                        <span className='ml-2'>{t('app_setting.feedback')}</span>
                    </Button>
                    <Button
                        type="text"
                        icon={<QuestionOutlined />}
                        onClick={handleGuide}
                        className='flex items-center justify-start w-full h-12 text-left'
                    >
                        <span className='ml-2'>{t('app_setting.guide')}</span>
                    </Button>
                </Space>
            </Modal>

            <Modal
                title={t('app_setting.settings')}
                open={showSettingsModal}
                onCancel={() => setShowSettingsModal(false)}
                footer={null}
                width={300}
            >
                <Space orientation='vertical' className='w-full'>
                    <div className='flex items-center justify-between w-full h-12 px-4'>
                        <span className='flex items-center'>
                            {theme === 'dark' ? <MoonOutlined /> : <SunOutlined />}
                            <span className='ml-2'>{t('app_setting.dark_mode', 'Dark Mode')}</span>
                        </span>
                        <Switch
                            checked={theme === 'dark'}
                            onChange={toggleTheme}
                            checkedChildren={<MoonOutlined />}
                            unCheckedChildren={<SunOutlined />}
                        />
                    </div>
                    {enablePayment && showPaymentButtons && <>
                        <Button
                            type="text"
                            icon={<DollarOutlined />}
                            onClick={() => {
                                setShowPayment(true);
                                setShowSettingsModal(false);
                            }}
                            className='flex items-center justify-start w-full h-12 text-left'
                        >
                            <span className='ml-2'>{t('app_setting.make_payment')}</span>
                        </Button>
                        <Button
                            type="text"
                            icon={<HistoryOutlined />}
                            onClick={() => {
                                setShowPaymentHistory(true);
                                setShowSettingsModal(false);
                            }}
                            className='flex items-center justify-start w-full h-12 text-left'
                        >
                            <span className='ml-2'>{t('app_setting.payment_history')}</span>
                        </Button>
                    </>}
                    {enableApiAccess && <>
                        <Button
                            type="text"
                            icon={<ApiOutlined />}
                            onClick={handleApiAccess}
                            className='flex items-center justify-start w-full h-12 text-left'
                        >
                            <span className='ml-2'>{t('app_setting.api_access')}</span>
                        </Button>
                    </>}
                    {canResetPassword && (
                        <Button
                            type="text"
                            icon={<KeyOutlined />}
                            onClick={handleResetPassword}
                            className='flex items-center justify-start w-full h-12 text-left'
                        >
                            <span className='ml-2'>{t('app_setting.reset_password')}</span>
                        </Button>
                    )}
                    {canDeleteAccount && (
                        <Button
                            type="text"
                            icon={<UserDeleteOutlined />}
                            onClick={handleDeregister}
                            danger
                            className='flex items-center justify-start w-full h-12 text-left'
                        >
                            <span className='ml-2'>{t('app_setting.delete_account')}</span>
                        </Button>
                    )}
                    <Button
                        type="primary"
                        icon={<LoginOutlined />}
                        onClick={handleLogoutClick}
                        danger
                        className='flex items-center justify-start w-full h-12 text-left'
                    >
                        <span className='ml-2'>{t('app_setting.logout')}</span>
                    </Button>
                </Space>
            </Modal>

            <Space orientation='vertical' align='center'>
                <Tooltip title={t('app_setting.help_tooltip')}>
                    <Button
                        shape="circle"
                        icon={<QuestionOutlined />}
                        onClick={() => setShowHelpModal(true)}
                        size='large'
                    />
                </Tooltip>

                <Tooltip title={t('app_setting.settings_tooltip')}>
                    <Button
                        shape="circle"
                        icon={<SettingOutlined />}
                        onClick={() => setShowSettingsModal(true)}
                        size='large'
                    />
                </Tooltip>
                <SignalIndicator />
            </Space>
        </div>
    );
};

export default AppNavigator;